/*
 * Copyright Higharc Inc. 2020
 *
 * The shader will output (1.0 - ambient occlusion) in the alpha channel.
 * Blending can be used to apply the AO to a regular rendering.
 */
import * as THREE from "three";
import type { ScreenSpaceViewNormalUniforms, ScreenSpaceViewPositionUniforms } from "./screenSpaceHelpersGLSL";
import {
  screenSpaceViewNormalDefineDefaults,
  screenSpaceViewNormalGLSL,
  screenSpaceViewNormalUniformDefaults,
  screenSpaceViewPositionDefineDefaults,
  screenSpaceViewPositionGLSL,
  screenSpaceViewPositionUniformDefaults,
} from "./screenSpaceHelpersGLSL";
import { UVVertexShader } from "./UVVertexShader";

export interface GTAOCommonUniforms {
  [key: string]: THREE.IUniform;
  depthInvSize: THREE.IUniform;
  gtaoWorldSpaceScale: THREE.IUniform;
  gtaoFalloffMinRelativeRadius: THREE.IUniform;
  gtaoFalloffMaxRelativeRadius: THREE.IUniform;
}

export const gtaoCommonUniformDefaults = {
  // Inverse of sampled depth buffer size in pixels - can also be thought as the size of one pixel in UV space.
  depthInvSize: { value: new THREE.Vector2(1 / 512, 1 / 512) },
  gtaoWorldSpaceScale: { value: 0.4 },
  gtaoFalloffMinRelativeRadius: { value: 0.0 },
  gtaoFalloffMaxRelativeRadius: { value: 1.0 },
};

export const gtaoCommonUniformGLSL = `
uniform vec2 depthInvSize;
uniform float gtaoWorldSpaceScale;
uniform float gtaoFalloffMinRelativeRadius;
uniform float gtaoFalloffMaxRelativeRadius;
`;

interface GTAOShaderUniforms
  extends ScreenSpaceViewPositionUniforms,
    ScreenSpaceViewNormalUniforms,
    GTAOCommonUniforms {
  [key: string]: THREE.IUniform;
  targetInvSize: THREE.IUniform;

  maxScreenSpaceKernelRadius: THREE.IUniform;
  randomSeed: THREE.IUniform;

  intensity: THREE.IUniform;

  viewWorldUp: THREE.IUniform;
  lowerIntensityOnVerticalSurfaces: THREE.IUniform;
}

interface GTAOShaderType {
  defines: Record<string, number>;
  uniforms: GTAOShaderUniforms;
  vertexShader: string;
  fragmentShader: string;
}

export const gtaoHelpersGLSL = `
// moving costly divide into const. Add 1.0 so that the last sample still gets some weight instead of being useless.
const float INV_NUM_AO_SAMPLES = 1.0 / (float(NUM_AO_SAMPLES) + 1.0);

// Uniform weighted visibility term for a slice based on two horizon angles.
// GTAO slide 59
float arcVisibilityUniformWeight( const in float horizonFromViewVectorAngle1, const in float horizonFromViewVectorAngle2 ) {
  return 2.0 - cos(horizonFromViewVectorAngle1) - cos(horizonFromViewVectorAngle2);
}

// Cosine weighted visibility term, taking into account foreshortening.
// "normalFromViewVector" is the angle between normal and the view vectors. Normal vector is projected to the slice.
// GTAO slides 61-62
float arcVisibilityCosineWeight( const in float horizonFromViewVectorAngle1, const in float horizonFromViewVectorAngle2, const in float normalFromViewVector ) {
  return 0.25 * (-cos(2.0 * horizonFromViewVectorAngle1 - normalFromViewVector) + cos(normalFromViewVector) + 2.0 * horizonFromViewVectorAngle1 * sin(normalFromViewVector)) +
         0.25 * (-cos(2.0 * horizonFromViewVectorAngle2 - normalFromViewVector) + cos(normalFromViewVector) + 2.0 * horizonFromViewVectorAngle2 * sin(normalFromViewVector));
}
`;

/**
 * The preceding code should set:
 * baseAngle
 * originalRadius (relative to framebuffer height)
 * radiusStep (relative to framebuffer height)
 * centerUv
 * centerViewPosition (in view space)
 * centerViewNormal (in view space)
 *
 * Depends on these uniforms:
 * depthInvSize
 * gtaoFalloffMinRelativeRadius
 * gtaoFalloffMaxRelativeRadius
 * gtaoWorldSpaceScale
 *
 * @param getSamplesChunk Shader code that takes in sampleUv1, sampleUv2, and sampleIndex, and initializes sampleDepth1 and sampleDepth2.
 * @returns GTAO implementation.
 */
export function gtaoAcculumateSlicesGLSL(getSamplesChunk: string): string {
  return `
vec3 viewVector = POINT_TO_CAMERA_DIRECTION(centerViewPosition);
float aspect = depthInvSize.x / depthInvSize.y;

float visibility = 0.00005;
float totalWeight = 0.0001;

float invFalloffRange = 1.0 / (gtaoFalloffMaxRelativeRadius - gtaoFalloffMinRelativeRadius);
float falloffMul = (2.0 / (gtaoWorldSpaceScale * gtaoWorldSpaceScale)) * invFalloffRange;
float falloffOffset = -gtaoFalloffMinRelativeRadius * invFalloffRange;

for (int angleIndex = 0; angleIndex < NUM_AO_ANGLE_SAMPLES; angleIndex++) {

  float angle = (baseAngle + float(angleIndex)) / float(NUM_AO_ANGLE_SAMPLES) * PI;

  vec2 sliceDirection = vec2(cos(angle), sin(angle));
  vec2 sliceUvDirection = sliceDirection * vec2(aspect, 1.0);

  vec2 horizons = vec2(-PI * 0.5);
  vec2 prevHorizonCandidates = vec2(-PI * 0.5);
  float radius = originalRadius;

  for(int sampleIndex = 0; sampleIndex < NUM_AO_SAMPLES; sampleIndex++) {
    vec2 sampleUv1 = centerUv + sliceUvDirection * radius;
    vec2 sampleUv2 = centerUv - sliceUvDirection * radius;
    radius += radiusStep;

    ${getSamplesChunk}

    vec2 mask = vec2(1.0, 1.0);
    if (IS_DEPTH_OUT_OF_BOUNDS(sampleDepth1)) {
      mask.x = 0.0;
    }
    vec3 sampleViewPosition1 = getViewPosition(sampleUv1, getViewZ(sampleDepth1));
    if (IS_DEPTH_OUT_OF_BOUNDS(sampleDepth2)) {
      if (mask.x == 0.0) {
        continue;
      }
      mask.y = 0.0;
    }
    vec3 sampleViewPosition2 = getViewPosition(sampleUv2, getViewZ(sampleDepth2));

    // Corresponds to "ds" on GTAO slide 54.
    vec3 sampleViewPositionFromCenterViewPosition1 = sampleViewPosition1 - centerViewPosition;
    vec3 sampleViewPositionFromCenterViewPosition2 = sampleViewPosition2 - centerViewPosition;

    // Attenuation mentioned in GTAO paper in section 4.
    vec2 diffsSquared = vec2(
      dot(sampleViewPositionFromCenterViewPosition1, sampleViewPositionFromCenterViewPosition1),
      dot(sampleViewPositionFromCenterViewPosition2, sampleViewPositionFromCenterViewPosition2));
    vec2 falloffs = clamp(diffsSquared * falloffMul + falloffOffset, 0.0, 1.0);

    vec2 horizonCandidates = vec2(
      dot(normalize(sampleViewPositionFromCenterViewPosition1), viewVector),
      dot(normalize(sampleViewPositionFromCenterViewPosition2), viewVector));

    // Thickness heuristic from the GTAO paper, tweaked to reduce haloing.
    // TODO: The paper doesn't give an exact formula for this, and one implementation for Unity also seems to use a different approach here.
    // Find out what's the best thing to do here.
    vec2 thicknessHeuristic = (1.0 - vec2(lessThan(horizonCandidates, prevHorizonCandidates)) * 0.7);
    horizons = mix(horizons, horizonCandidates, (1.0 - falloffs) * thicknessHeuristic * mask);
    prevHorizonCandidates = horizonCandidates;
  }

  vec3 slicePlaneNormal = normalize(cross(vec3(sliceDirection, 0.0), viewVector));
  vec3 sliceTangent = cross(viewVector, slicePlaneNormal);
  vec3 projectedNormal = centerViewNormal - slicePlaneNormal * dot(centerViewNormal, slicePlaneNormal);
  vec3 normalizedProjNormal = normalize(projectedNormal);
  float n_sign = dot(normalizedProjNormal, sliceTangent);

  horizons = acos(clamp(horizons, -1.0, 1.0));

  // Clamp horizon angles to the half-sphere relative to the surface normal.
  float cos_n = clamp(dot(normalizedProjNormal, viewVector), -1.0, 1.0);
  float n = -sign(n_sign) * acos(cos_n);
  horizons.x = n + max(-horizons.x - n, -PI * 0.5);
  horizons.y = n + min(horizons.y - n, PI * 0.5);

  float sampleWeight = length(projectedNormal);

  #if AO_COSINE_WEIGHTING == 1
  visibility += arcVisibilityCosineWeight(horizons.x, horizons.y, n) * sampleWeight;
  #else
  visibility += 0.5 * arcVisibilityUniformWeight(horizons.x, horizons.y) * sampleWeight;
  #endif

  totalWeight += sampleWeight;
}
`;
}

export const GTAOShader: GTAOShaderType = {
  defines: {
    ...screenSpaceViewPositionDefineDefaults,
    ...screenSpaceViewNormalDefineDefaults,

    // Number of depth mip levels to use.
    // They're passed to the shader as separate samplers, since due to WebGL 1.0 limitations we can't use a mipmapped NPOT texture.
    DEPTH_MIPS: 1,

    NUM_AO_SAMPLES: 4, // Value set with TAA in mind, without TAA 7 is suggested.
    NUM_AO_ANGLE_SAMPLES: 2,

    // If true then GTAO visibility is weighted with foreshortening taken into account.
    AO_COSINE_WEIGHTING: 0,
  },
  uniforms: {
    ...screenSpaceViewPositionUniformDefaults,
    ...screenSpaceViewNormalUniformDefaults,

    ...gtaoCommonUniformDefaults,

    // Inverse of the target buffer size in pixels.
    targetInvSize: { value: new THREE.Vector2(1 / 512, 1 / 512) },

    maxScreenSpaceKernelRadius: { value: 0.1 },
    randomSeed: { value: 0.0 },

    intensity: { value: 0.1 },

    // World up direction in view space.
    viewWorldUp: { value: new THREE.Vector3(0, 1, 0) },
    lowerIntensityOnVerticalSurfaces: { value: 0.3 },
  },
  vertexShader: UVVertexShader,

  fragmentShader: `#include <common>
    #include <packing>

    varying vec2 vUv;

    // Gets set to vUv with jitter applied.
    vec2 centerUv;

    uniform sampler2D tDepth[DEPTH_MIPS];

    float unpackRGBAToDepthExceptMip0( const in vec4 depth, int mipIndex ) {
      return mipIndex > 0 ? unpackRGBAToDepth( depth ) : depth.x;
    }

    // This is a macro and not a function to work within WebGL 1.0 shader limitations.
    // We can't pass the index to the sampler array to a function.
    #if DEPTH_TEX_CONTAINS_VIEWZ == 1 || (DEPTH_PACKING == 0 && DEPTH_MIPS <= 1)
      #define GET_DEPTH(mipIndex, screenPosition) texture2D( tDepth[mipIndex], screenPosition ).x
    #elif DEPTH_PACKING == 1
      #define GET_DEPTH(mipIndex, screenPosition) unpackRGBAToDepth( texture2D( tDepth[mipIndex], screenPosition ) )
    #else
      // Mip levels after the 0 level are always packed to RGBA.
      #define GET_DEPTH(mipIndex, screenPosition) unpackRGBAToDepthExceptMip0( texture2D( tDepth[mipIndex], screenPosition ), mipIndex )
    #endif

    ${screenSpaceViewPositionGLSL}
    ${screenSpaceViewNormalGLSL}

    uniform vec2 targetInvSize;

    uniform float randomSeed;
    uniform float maxScreenSpaceKernelRadius;

    uniform float intensity;

    uniform vec3 viewWorldUp;
    uniform float lowerIntensityOnVerticalSurfaces;

    ${gtaoCommonUniformGLSL}
    ${gtaoHelpersGLSL}

    float getVisibility(const in vec3 centerViewPosition, const in vec3 centerViewNormal) {

      INIT_Y_RELATIVE_RADIUS_FROM_WORLD_SPACE(originalRadius, gtaoWorldSpaceScale, centerViewPosition.z, cameraProjectionMatrix);
      // The maxScreenSpaceKernelRadius parameter is just to keep performance in check.
      originalRadius = min(originalRadius, maxScreenSpaceKernelRadius);
      originalRadius *= INV_NUM_AO_SAMPLES;
      float radiusStep = originalRadius;

      float checkerboard = mod(floor(gl_FragCoord.x) + floor(gl_FragCoord.y), 2.0);
      float baseAngle = (checkerboard + rand( centerUv + randomSeed )) * 0.5;

      // Adding a random term to radius makes the rendering converge to a nicer result after TAA without banding
      // artifacts caused by using constant radiuses.
      #if NUM_AO_SAMPLES <= 8
      //    originalRadius += radiusStep * ( rand( centerUv - 0.5 + randomSeed ) - 0.5 );
      // Optimized formula for randomizing the radius step - this has a risk of introducing slight artifacts,
      // but in practice seems to be performing well enough:
      originalRadius += radiusStep * ( fract( baseAngle * 317.0 ) - 0.5 );
      #endif

      // Radius must be at least so high that the same pixel can't be sampled twice from the depth buffer.
      originalRadius = max( originalRadius, depthInvSize.y * 1.5 );

      ${gtaoAcculumateSlicesGLSL(`
#if DEPTH_MIPS > 1
// TODO: This code path doesn't work on WebGL 2.0, because it requires constant indices for sampler arrays.
// We need to unroll the loop or store the textures in an actual mip chain to support WebGL 2.0 here.
float sampleDepth1 = GET_DEPTH( (sampleIndex * DEPTH_MIPS) / NUM_AO_SAMPLES, sampleUv1 );
#else
float sampleDepth1 = GET_DEPTH( 0, sampleUv1 );
#endif

#if DEPTH_MIPS > 1
// TODO: This code path doesn't work on WebGL 2.0, because it requires constant indices for sampler arrays.
// We need to unroll the loop or store the textures in an actual mip chain to support WebGL 2.0 here.
float sampleDepth2 = GET_DEPTH( (sampleIndex * DEPTH_MIPS) / NUM_AO_SAMPLES, sampleUv2 );
#else
float sampleDepth2 = GET_DEPTH( 0, sampleUv2 );
#endif
`)}

      return visibility / totalWeight;
    }

    void main() {
      // Jitter center UV so that our center position is placed at a nearby depth buffer sample center.
      // This should convert some systematic artifacts to random noise.
      vec2 randVec = vec2(rand(vUv - 0.7 + randomSeed), rand(vUv - 0.3 + randomSeed));
      vec2 depthSize = 1.0 / depthInvSize;
      vec2 sampleAreaInPixels = targetInvSize * depthSize;
      vec2 samplePixelOffset = (randVec - 0.5) * sampleAreaInPixels;
      centerUv = (floor(vUv * depthSize + samplePixelOffset) + 0.5) * depthInvSize;

      float centerDepth = GET_DEPTH(0, centerUv);
      if (IS_DEPTH_OUT_OF_BOUNDS(centerDepth)) {
        discard;
      }

      vec3 viewPosition = getViewPosition(centerUv, getViewZ(centerDepth));
      vec3 viewNormal = GET_VIEW_NORMAL(viewPosition, centerUv);

      float normalVerticality = abs(dot(viewWorldUp, viewNormal));
      float totalIntensity = (1.0 - (1.0 - normalVerticality) * lowerIntensityOnVerticalSurfaces) * intensity;
      float visibility = mix(1.0, getVisibility( viewPosition, viewNormal ), totalIntensity);
      gl_FragColor = vec4(visibility, packNormalToRGB(viewNormal));
    }`,
} as const;
